import React, { useState, useEffect, useRef } from 'react';
import './RankingModal.css';

interface RankingItem {
    rank: number;
    id: number | string;
    name: string;
    branchCity: string;
    progress: number;
    remainingVisits: number;
    completedVisits: number;
    totalWorkplaces: number;
    completionRate: number;
    qualityScore?: number;
    rejectionRate?: number;
    approvedDocsCount?: number;
    rejectedDocsCount?: number;
    pendingDocsCount?: number;
    totalRequiredDocs?: number;
    totalCompletedDocs?: number;
    docCompletionRate?: number;
    docScore?: number;
    compositeScore?: number;
    isMe: boolean;
    displayValue: string;
}

interface RankingResponse {
    hasMultipleBranches: boolean;
    userBranch: string;
    metric: 'composite' | 'progress' | 'remaining' | 'quality' | 'documents';
    branchScope: 'my' | 'all' | 'branches';
    myRank: number;
    totalCount: number;
    rankings: RankingItem[];
}

interface RankingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRankLoaded?: (rank: number) => void;
    isStaffView?: boolean;
}

const RankingModal: React.FC<RankingModalProps> = ({ isOpen, onClose, onRankLoaded, isStaffView = false }) => {
    const [metric, setMetric] = useState<'composite' | 'progress' | 'remaining' | 'quality' | 'documents'>('composite');
    const [branchScope, setBranchScope] = useState<'my' | 'all' | 'branches'>(isStaffView ? 'all' : 'my');
    const [personnelType, setPersonnelType] = useState<'expert' | 'doctor'>('expert');
    const [data, setData] = useState<RankingResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showInfo, setShowInfo] = useState(false);

    const myCardRef = useRef<HTMLDivElement>(null);

    const fetchRankings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('fieldToken');
            const ts = Date.now();
            const pQuery = isStaffView ? `&personnelType=${personnelType}` : '';
            const response = await fetch(`/api/mobile/rankings?metric=${metric}&branchScope=${branchScope}${pQuery}&t=${ts}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                cache: 'no-store'
            });

            if (response.ok) {
                const resData: RankingResponse = await response.json();
                setData(resData);
                if (resData.myRank > 0) {
                    sessionStorage.setItem('field_my_rank', String(resData.myRank));
                    onRankLoaded?.(resData.myRank);
                }
            } else {
                setError('Sıralama verileri alınamadı.');
            }
        } catch (err) {
            console.error('Sıralama getirme hatası:', err);
            setError('Bağlantı hatası oluştu.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchRankings();
        }
    }, [isOpen, metric, branchScope, personnelType]);

    // Auto-scroll to user's card when data is loaded
    useEffect(() => {
        if (!isStaffView && data && myCardRef.current) {
            const timer = setTimeout(() => {
                myCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [data, loading, isStaffView]);

    if (!isOpen) return null;

    const getRankBadge = (rank: number) => {
        if (rank === 1) return { icon: '🥇', class: 'gold' };
        if (rank === 2) return { icon: '🥈', class: 'silver' };
        if (rank === 3) return { icon: '🥉', class: 'bronze' };
        return { icon: `#${rank}`, class: 'normal' };
    };

    return (
        <div className="ranking-modal-overlay" onClick={onClose}>
            <div className="ranking-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="ranking-modal-header">
                    <div className="ranking-header-title">
                        <span className="ranking-trophy">🏆</span>
                        <h2>OSGB Başarı Sıralaması</h2>
                    </div>
                    <div className="ranking-header-actions">
                        <button
                            type="button"
                            className="ranking-info-btn"
                            title="Genel Başarı Algoritması Bilgisi"
                            onClick={() => setShowInfo(true)}
                        >
                            ℹ️ Nasıl Hesaplanır?
                        </button>
                        <button className="ranking-close-btn" onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* Subtitle / User Rank Summary Banner */}
                {data && data.myRank > 0 && !isStaffView && (
                    <div className="ranking-my-summary">
                        <div className="summary-badge">
                            {branchScope === 'branches' ? (
                                <>🏆 Şubeniz <span>{data.totalCount}</span> şube arasında <strong>{data.myRank}.</strong> sırada</>
                            ) : (
                                <>🎉 <span>{data.totalCount}</span> kişi arasında <strong>{data.myRank}.</strong> sıradasınız</>
                            )}
                        </div>
                        {data.userBranch && (
                            <span className="user-branch-tag">📍 {data.userBranch} Şubesi</span>
                        )}
                    </div>
                )}

                {/* Coordinator / Auditor General List Info Banner */}
                {isStaffView && data && (
                    <div className="ranking-my-summary staff-summary">
                        <div className="summary-badge staff-badge">
                            👥 Toplam <strong>{data.totalCount}</strong> {branchScope === 'branches' ? 'Şube' : (personnelType === 'doctor' ? 'İşyeri Hekimi' : 'İSG Uzmanı')} Listeleniyor
                        </div>
                        {data.userBranch && branchScope === 'my' && (
                            <span className="user-branch-tag">📍 {data.userBranch}</span>
                        )}
                    </div>
                )}

                {/* Controls Section */}
                <div className="ranking-controls">
                    {/* Coordinator / Auditor Personnel Selector */}
                    {isStaffView && branchScope !== 'branches' && (
                        <div className="ranking-personnel-toggle-group">
                            <button
                                type="button"
                                className={`personnel-toggle-tab ${personnelType === 'expert' ? 'active' : ''}`}
                                onClick={() => setPersonnelType('expert')}
                            >
                                👷 İSG Uzmanları
                            </button>
                            <button
                                type="button"
                                className={`personnel-toggle-tab ${personnelType === 'doctor' ? 'active' : ''}`}
                                onClick={() => setPersonnelType('doctor')}
                            >
                                🩺 İşyeri Hekimleri
                            </button>
                        </div>
                    )}

                    {/* Branch Filter Checkboxes */}
                    {data?.hasMultipleBranches && (
                        <div className="ranking-branch-selector">
                            <label className={`branch-check-item ${branchScope === 'my' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="branchScope"
                                    checked={branchScope === 'my'}
                                    onChange={() => setBranchScope('my')}
                                />
                                <span className="custom-check"></span>
                                📍 Kendi Şubeniz ({data.userBranch})
                            </label>

                            <label className={`branch-check-item ${branchScope === 'all' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="branchScope"
                                    checked={branchScope === 'all'}
                                    onChange={() => setBranchScope('all')}
                                />
                                <span className="custom-check"></span>
                                🌐 Tüm Şubeler (Personel)
                            </label>

                            <label className={`branch-check-item ${branchScope === 'branches' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="branchScope"
                                    checked={branchScope === 'branches'}
                                    onChange={() => setBranchScope('branches')}
                                />
                                <span className="custom-check"></span>
                                🏆 Şubeler Arası Yarışma
                            </label>
                        </div>
                    )}

                    {/* Metric Buttons - 2 Tier Hybrid Layout */}
                    <div className="ranking-metric-container">
                        <button
                            className={`metric-btn composite-btn ${metric === 'composite' ? 'active' : ''}`}
                            onClick={() => setMetric('composite')}
                        >
                            <span className="btn-star">⭐</span> GENEL BAŞARI <span className="ai-tag">AI</span>
                        </button>

                        <div className="ranking-sub-metric-grid">
                            <button
                                className={`metric-btn ${metric === 'progress' ? 'active' : ''}`}
                                onClick={() => setMetric('progress')}
                            >
                                📊 İlerleme
                            </button>
                            <button
                                className={`metric-btn ${metric === 'remaining' ? 'active' : ''}`}
                                onClick={() => setMetric('remaining')}
                            >
                                🕒 Kalan Ziy.
                            </button>
                            <button
                                className={`metric-btn ${metric === 'quality' ? 'active' : ''}`}
                                onClick={() => setMetric('quality')}
                            >
                                🛡️ Belge Kalitesi
                            </button>
                            <button
                                className={`metric-btn ${metric === 'documents' ? 'active' : ''}`}
                                onClick={() => setMetric('documents')}
                            >
                                📄 Belge Sayısı
                            </button>
                        </div>
                    </div>
                </div>

                {/* Body / List Section */}
                <div className="ranking-modal-body">
                    {loading ? (
                        <div className="ranking-loading">
                            <div className="ranking-spinner"></div>
                            <p>Sıralama hesaplanıyor...</p>
                        </div>
                    ) : error ? (
                        <div className="ranking-error">{error}</div>
                    ) : (
                        <div className="ranking-list">
                            {data?.rankings.map((item) => {
                                const badge = getRankBadge(item.rank);
                                return (
                                    <div
                                        key={item.id}
                                        ref={item.isMe ? myCardRef : undefined}
                                        className={`ranking-card ${item.isMe ? 'is-me' : ''}`}
                                    >
                                        <div className={`rank-number-badge ${badge.class}`}>
                                            {badge.icon}
                                        </div>

                                        <div className="ranking-card-info">
                                            <div className="personnel-name">
                                                {item.name}
                                                {item.isMe && <span className="you-pill">{branchScope === 'branches' ? 'ŞUBENİZ' : 'SİZ'}</span>}
                                            </div>
                                            <div className="personnel-subtext">
                                                {data.hasMultipleBranches && branchScope !== 'branches' && (
                                                    <span className="personnel-branch">📍 {item.branchCity}</span>
                                                )}
                                                {metric === 'composite' && item.totalWorkplaces > 0 && (
                                                    <span className="personnel-meta">
                                                        {item.totalWorkplaces} İşyeri • %{item.progress} İlerleme
                                                    </span>
                                                )}
                                                {metric === 'documents' && item.totalWorkplaces > 0 && (
                                                    <span className="personnel-meta">
                                                        {item.totalWorkplaces} İşyeri • Hedef: {item.totalRequiredDocs} Belge
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="ranking-card-val">
                                            <span className={`value-tag ${metric === 'composite' ? 'composite-tag' : metric === 'quality' ? 'quality-tag' : metric === 'documents' ? 'doc-tag' : ''}`}>
                                                {item.displayValue}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="ranking-modal-footer">
                    <button className="ranking-done-btn" onClick={onClose}>Kapat</button>
                </div>

                {/* Info / Explanation Popup Overlay */}
                {showInfo && (
                    <div className="ranking-info-overlay" onClick={() => setShowInfo(false)}>
                        <div className="ranking-info-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="ranking-info-header">
                                <h3>⭐ GENEL BAŞARI (AI) Algoritması</h3>
                                <button className="ranking-info-close" onClick={() => setShowInfo(false)}>✕</button>
                            </div>
                            <div className="ranking-info-content">
                                <p className="info-lead">
                                    Genel Başarı puanı, sahadaki tüm operasyonel performansınızı adil ve çok kriterli bir yapay zeka algoritmasıyla değerlendirir:
                                </p>
                                <div className="info-pillar">
                                    <span className="pillar-icon">📊</span>
                                    <div>
                                        <strong>%25 İlerleme Payı:</strong> İşyerlerinizin tehlike sınıfına göre (Çok Tehlikeli 1.5x, Tehlikeli 1.2x, Az Tehlikeli 0.70x) ağırlıklandırılmış tamamlanma yüzdesidir.
                                    </div>
                                </div>
                                <div className="info-pillar">
                                    <span className="pillar-icon">🕒</span>
                                    <div>
                                        <strong>%25 Ziyaret Hızı:</strong> Bulunulan ay içinde yapılması zorunlu olan saha ziyaretlerinin tamamlanma oranıdır.
                                    </div>
                                </div>
                                <div className="info-pillar">
                                    <span className="pillar-icon">🛡️</span>
                                    <div>
                                        <strong>%25 Belge Kalitesi:</strong> Yüklediğiniz evrakların denetçi onay oranıdır. 85 taban puanla başlar; onaylı evraklarla 100'e yükselir. Reddedilen evraklar puanı düşürür.
                                    </div>
                                </div>
                                <div className="info-pillar">
                                    <span className="pillar-icon">📄</span>
                                    <div>
                                        <strong>%25 Belge Sayısı:</strong> Atanmış işyerlerinizde yapılması zorunlu standart belgelerin (iptal edilenler hariç tutularak) güncel ve eksiksiz tamamlanma oranıdır.
                                    </div>
                                </div>
                                <div className="info-pillar">
                                    <span className="pillar-icon">⚡</span>
                                    <div>
                                        <strong>Saha Proaktivite Çarpanı:</strong> Tehlikeli işyerlerini düzenli takip eden personele +%10'a varan ödül çarpanı uygulanır. İşyerinin atanma tarihinden önceki aylar hesaba katılmaz.
                                    </div>
                                </div>
                                <div className="info-pillar">
                                    <span className="pillar-icon">⚖️</span>
                                    <div>
                                        <strong>Takvim Dengelemesi:</strong> Ayın gününe göre ziyaret beklentisi dengelenir; ay başında henüz yapılmayan ziyaretler için haksız standart sapma cezası kesilmez.
                                    </div>
                                </div>
                            </div>
                            <div className="ranking-info-footer">
                                <button className="ranking-info-ok-btn" onClick={() => setShowInfo(false)}>Anladım</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RankingModal;
